import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { formatAmount } from '../../lib/format';

interface CashierReportProps {
  entityId: string;
  periodId: string;
}

interface DailyData {
  opening: number;
  in: number;
  out: number;
  closing: number;
  check: number;
}

interface WalletData {
  walletId: string;
  walletName: string;
  currency: string;
  daily: Record<string, DailyData>;
}

interface CashierData {
  dates: string[];
  byType: Record<string, WalletData[]>;
  period: { start: string; end: string };
}

export default function CashierReport({ entityId, periodId }: CashierReportProps) {
  const { t } = useTranslation('reports');
  const [searchParams, setSearchParams] = useSearchParams();
  const days = parseInt(searchParams.get('days') || '14', 10) || 14;

  const setDays = useCallback((d: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (d === 14) next.delete('days'); else next.set('days', String(d));
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const periodParam = periodId ? `&periodId=${periodId}` : '';

  const { data, isLoading } = useQuery<CashierData>({
    queryKey: ['report-cashier', entityId, periodId, days],
    queryFn: async () => {
      const res = await fetch(`/api/reports/cashier?days=${days}${periodParam}`, {
        headers: { 'X-Entity-Id': entityId },
        credentials: 'include',
      });
      return res.json();
    },
  });

  if (isLoading) return <LoadingState message={t('cashier.loading')} />;

  if (!data?.dates?.length || !data?.byType || Object.keys(data.byType).length === 0) {
    return (
      <div className="card">
        <EmptyState title={t('cashier.empty.title')} description={t('cashier.empty.description')} />
      </div>
    );
  }

  // Format date for column header (e.g., "15 Dec" or "15 дек")
  const formatDateHeader = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = d.toLocaleDateString(undefined, { month: 'short' });
    return `${day} ${month}`;
  };

  // Type labels and colors
  const typeConfig: Record<string, { label: string; color: string; darkColor: string }> = {
    cash: { label: t('balances.cash'), color: '#c8e6c9', darkColor: '#2e7d32' },
    bank: { label: t('balances.bank'), color: '#bbdefb', darkColor: '#1565c0' },
    crypto: { label: t('balances.crypto'), color: '#e1bee7', darkColor: '#7b1fa2' },
    payment_system: { label: t('balances.paymentSystems'), color: '#ffe0b2', darkColor: '#ef6c00' },
  };

  // Row labels for each metric
  const metricRows = [
    { key: 'opening', label: t('cashier.openingBalance'), className: 'row-opening' },
    { key: 'in', label: t('cashier.in'), className: 'row-in' },
    { key: 'out', label: t('cashier.out'), className: 'row-out' },
    { key: 'closing', label: t('cashier.closingBalance'), className: 'row-closing' },
    { key: 'check', label: t('cashier.check'), className: 'row-check' },
  ];

  return (
    <div className="report-content">
      <div className="card mb-4">
        <div className="controls-row">
          <span className="control-label">{t('pnl.period')}:</span>
          <select
            className="form-input"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value="7">7 {t('balances.days')}</option>
            <option value="14">14 {t('balances.days')}</option>
            <option value="30">30 {t('balances.days')}</option>
            <option value="90">90 {t('balances.days')}</option>
          </select>
        </div>
      </div>

      <div className="card">
        <div className="cashier-table-wrapper">
          <table className="cashier-table">
            <thead>
              <tr>
                <th className="header-wallet">{t('columns.wallet')}</th>
                <th className="header-currency">{t('columns.currency')}</th>
                <th className="header-metric"></th>
                {data.dates.map((date) => (
                  <th key={date} className="header-date">{formatDateHeader(date)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.byType).map(([type, wallets]) => {
                const config = typeConfig[type] || { label: type, color: '#e0e0e0', darkColor: '#616161' };
                return (
                  <>
                    {/* Type Header */}
                    <tr
                      key={`type-${type}`}
                      className="type-header"
                      style={{ '--type-color': config.color, '--type-dark-color': config.darkColor } as React.CSSProperties}
                    >
                      <td colSpan={3 + data.dates.length}>{config.label}</td>
                    </tr>

                    {/* Wallet rows */}
                    {wallets.map((wallet) => (
                      <>
                        {metricRows.map((metric, idx) => (
                          <tr key={`${wallet.walletId}-${metric.key}`} className={metric.className}>
                            {idx === 0 && (
                              <>
                                <td className="cell-wallet" rowSpan={metricRows.length}>
                                  {wallet.walletName}
                                </td>
                                <td className="cell-currency" rowSpan={metricRows.length}>
                                  {wallet.currency}
                                </td>
                              </>
                            )}
                            <td className="cell-metric">{metric.label}</td>
                            {data.dates.map((date) => {
                              const dayData = wallet.daily[date] || { opening: 0, in: 0, out: 0, closing: 0, check: 0 };
                              const value = dayData[metric.key as keyof DailyData];
                              const isCheck = metric.key === 'check';
                              const isBalanced = isCheck && Math.abs(value) < 0.01;
                              const hasDiscrepancy = isCheck && !isBalanced;

                              return (
                                <td
                                  key={date}
                                  className={`cell-value ${
                                    metric.key === 'in' ? 'text-success' :
                                    metric.key === 'out' ? 'text-error' :
                                    hasDiscrepancy ? 'text-error cell-discrepancy' :
                                    isBalanced ? 'text-success' : ''
                                  }`}
                                >
                                  {isCheck
                                    ? (isBalanced ? '✓' : formatAmount(value.toString()))
                                    : (value !== 0 ? formatAmount(value.toString()) : '-')
                                  }
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        {/* Spacer row between wallets */}
                        <tr className="wallet-spacer">
                          <td colSpan={3 + data.dates.length}></td>
                        </tr>
                      </>
                    ))}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="check-formula">
          <small>{t('checkFormula')}</small>
        </div>
      </div>

      <style>{`
        .controls-row {
          display: flex;
          gap: 12px;
          align-items: center;
        }
        .control-label {
          font-size: 14px;
          color: var(--color-text-muted);
        }

        .cashier-table-wrapper {
          overflow-x: auto;
          overflow-y: visible;
        }

        .cashier-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
          white-space: nowrap;
        }

        .cashier-table th,
        .cashier-table td {
          border: 1px solid var(--color-border);
          padding: 4px 8px;
        }

        .cashier-table thead th {
          background: var(--color-table-header);
          font-weight: 600;
          text-align: center;
        }

        .header-wallet {
          text-align: left !important;
          min-width: 150px;
        }

        .header-currency {
          min-width: 60px;
        }

        .header-metric {
          min-width: 100px;
        }

        .header-date {
          min-width: 70px;
        }

        .type-header {
          background: var(--type-color) !important;
          font-weight: 600;
        }

        .type-header td {
          padding: 8px 10px;
        }

        .cell-wallet {
          text-align: left;
          font-weight: 500;
          vertical-align: middle;
          background: var(--color-surface);
        }

        .cell-currency {
          text-align: center;
          font-weight: 500;
          vertical-align: middle;
          background: var(--color-surface);
        }

        .cell-metric {
          text-align: left;
          font-size: 10px;
          color: var(--color-text-muted);
          padding-left: 12px !important;
        }

        .cell-value {
          text-align: right;
          font-family: monospace;
        }

        .row-opening {
          background: #f5f5f5;
        }

        .row-in td:not(.cell-wallet):not(.cell-currency) {
          background: #e8f5e9;
        }

        .row-out td:not(.cell-wallet):not(.cell-currency) {
          background: #ffebee;
        }

        .row-closing {
          background: #e3f2fd;
          font-weight: 500;
        }

        .row-check td:not(.cell-wallet):not(.cell-currency) {
          background: #fff8e1;
        }

        .cell-discrepancy {
          background: #ffcdd2 !important;
          font-weight: 600;
        }

        .wallet-spacer {
          height: 4px;
          background: transparent !important;
        }

        .wallet-spacer td {
          border: none !important;
          padding: 0 !important;
        }

        .text-success {
          color: var(--color-success);
        }

        .text-error {
          color: var(--color-error);
        }

        .check-formula {
          margin-top: 16px;
          padding: 8px 12px;
          background: var(--color-gray-50);
          border-radius: var(--radius);
          color: var(--color-text-muted);
        }

        /* Dark theme adjustments */
        :root[data-theme="dark"] .type-header {
          background: var(--type-dark-color) !important;
          color: white;
        }

        :root[data-theme="dark"] .row-opening {
          background: rgba(255, 255, 255, 0.05);
        }

        :root[data-theme="dark"] .row-in td:not(.cell-wallet):not(.cell-currency) {
          background: rgba(46, 125, 50, 0.15);
        }

        :root[data-theme="dark"] .row-out td:not(.cell-wallet):not(.cell-currency) {
          background: rgba(198, 40, 40, 0.15);
        }

        :root[data-theme="dark"] .row-closing {
          background: rgba(25, 118, 210, 0.15);
        }

        :root[data-theme="dark"] .row-check td:not(.cell-wallet):not(.cell-currency) {
          background: rgba(255, 193, 7, 0.15);
        }

        :root[data-theme="dark"] .cell-discrepancy {
          background: rgba(198, 40, 40, 0.3) !important;
        }

        :root[data-theme="dark"] .cell-wallet,
        :root[data-theme="dark"] .cell-currency {
          background: var(--color-surface);
        }
      `}</style>
    </div>
  );
}
